import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnDestroy, inject, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, interval } from 'rxjs';
import { IMediaYouTube } from 'src/app/api/modules/core/dynamic/components/IMedia';
import { createDigitaServiceError } from 'src/app/app-error';
import { MediaRepository } from '../media.repository';
import { MediaService } from '../media.service';

/**
 * The Media YouTube Provider Component.
 *
 * It is not standalone and belongs within the {@link MediaComponent}.
 *
 * See {@link https://developers.google.com/youtube/iframe_api_reference}
 */
@Component({
  selector: 'app-media-youtube',
  templateUrl: './media-youtube.component.html',
  styleUrls: ['./media-youtube.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaYouTubeComponent implements AfterViewInit, OnDestroy {
  private readonly mediaRepository = inject(MediaRepository);
  private readonly mediaService = inject(MediaService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * A reference to the div the YouTube API converts into the player iframe.
   */
  private readonly mediaElement = viewChild.required<ElementRef<HTMLDivElement>>('media');

  /**
   * Holds a reference to the youtube player
   */
  private player?: YT.Player;

  /**
   * Polls the player for the current time while the media is playing.
   */
  private timerSubscription?: Subscription;

  /////////////////////////////////////////////////////////////////////
  // LIFECYCLE
  /////////////////////////////////////////////////////////////////////

  /**
   * Lifecycle
   */
  ngAfterViewInit() {
    // this component only renders once the media is initialized, so the settings are guaranteed to exist
    const options = this.mediaRepository.youtubeSettings();
    if (!options) {
      throw createDigitaServiceError('MediaYouTubeComponent', 'ngAfterViewInit', 'No YouTube settings available.', 'internal');
    }
    this.createPlayer(options);
  }

  /**
   * Lifecycle
   */
  ngOnDestroy() {
    this.timerSubscription?.unsubscribe();
    this.player?.destroy();
  }

  /////////////////////////////////////////////////////////////////////
  // PLAYER CREATION
  /////////////////////////////////////////////////////////////////////

  /**
   * Create the player.
   *
   * @param options - the youtube options
   */
  private createPlayer(options: IMediaYouTube) {
    // create the player
    this.player = new YT.Player(this.mediaElement().nativeElement, options);

    // listen to events on the player
    this.player.addEventListener('onReady', () => {
      // once the player is ready we will find the total time of the video
      this.updateDuration();

      // inform the store that the video is ready
      this.mediaService.applyReady();
    });

    this.player.addEventListener('onStateChange', (event: YT.OnStateChangeEvent) => {
      switch (event.data) {
        case YT.PlayerState.UNSTARTED:
          break;
        case YT.PlayerState.ENDED:
          this.videoEnded();
          break;
        case YT.PlayerState.PLAYING:
          this.videoPlaying();
          break;
        case YT.PlayerState.PAUSED:
          this.videoPaused();
          break;
      }
    });

    this.player.addEventListener('onError', (event: YT.OnErrorEvent) => {
      this.onError(event);
    });

    // create the integration
    this.createPlayerIntegration();
  }

  /**
   * Integrates the player with the MediaService of the application.
   *
   * The player methods are guarded because the YouTube API attaches them to the
   * player object asynchronously; a command can arrive before they exist.
   */
  private createPlayerIntegration() {
    // listen for play events.
    this.mediaService.play$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.player?.playVideo?.();
    });

    // listen for pause events.
    this.mediaService.pause$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.player?.pauseVideo?.();
    });

    // seek to the current time
    this.mediaService.seekTo$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data && this.player) {
        this.player.seekTo(data.seconds, data.allowSeekAhead);
        if (data.play) {
          this.player.playVideo?.();
        } else {
          this.player.pauseVideo?.();
        }
      }
    });

    // mute the media
    this.mediaService.mute$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.player?.mute?.();
    });

    // unMute the media
    this.mediaService.unMute$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.player?.unMute?.();
    });

    // set the volume
    this.mediaService.volume$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((volume) => {
      if (typeof volume === 'number' && volume >= 0 && volume <= 100) {
        this.player?.setVolume?.(volume);
      }
    });
  }

  /**
   * The Video has ended.
   */
  private videoEnded() {
    this.stopTimer(false);
    if (this.player) {
      const duration = Math.floor(this.player.getDuration());
      this.mediaService.applyCurrentTime(duration);
    }
    this.mediaService.applyEnded();
  }

  /**
   * Occurs when the video is playing.
   */
  private videoPlaying() {
    this.startTimer();
    this.mediaService.applyPlaying();
  }

  /**
   * Occurs when the video is paused.
   */
  private videoPaused() {
    this.stopTimer(true);
    this.mediaService.applyPaused();
  }

  /////////////////////////////////////////////////////////////////////
  // TIMERS
  /////////////////////////////////////////////////////////////////////

  /**
   * Start the playback timer.
   *
   * This will poll the video for currentTime Events.
   */
  private startTimer() {
    // stop any timer before starting a new one.
    this.stopTimer(true);

    // start a timer to check the current time of the video
    this.timerSubscription = interval(500)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateCurrentTime();
      });
  }

  /**
   * Stop the playback timer.
   *
   * @param updateCurrentTime - whether to update the current time of the video before stopping.
   */
  private stopTimer(updateCurrentTime: boolean) {
    // update the current time of the video before stopping.
    if (updateCurrentTime) {
      this.updateCurrentTime();
    }

    // stop the timer
    this.timerSubscription?.unsubscribe();
  }

  /////////////////////////////////////////////////////////////////////
  // UTILS
  /////////////////////////////////////////////////////////////////////

  /**
   * Retrieve the current time from the player and save it to the store.
   */
  private updateCurrentTime() {
    if (this.player) {
      const currentTime = Math.floor(this.player.getCurrentTime());
      this.mediaService.applyCurrentTime(currentTime);
    }
  }

  /**
   * Retrieve the total duration from the player and save it to the store.
   */
  private updateDuration() {
    if (this.player) {
      const duration = Math.floor(this.player.getDuration());
      this.mediaService.applyDuration(duration);
    }
  }

  /////////////////////////////////////////////////////////////////////
  // ERRORS
  /////////////////////////////////////////////////////////////////////

  /**
   * Occurs when an error occurs.
   *
   * @param event - the event
   */
  private onError(event: YT.OnErrorEvent) {
    this.mediaService.applyError(event.data?.toString());
  }
}
